import React, { useState, useEffect, useMemo } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Avatar,
  Tabs,
  Tab,
  Grid,
  Paper,
  Divider,
  Button,
  Skeleton,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Edit as EditIcon, Refresh as RefreshIcon } from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import { User, Outfit } from "../types";
import OutfitCard from "../components/Outfit/OutfitCard";
import InFeedAdGrid from "../components/Ads/InFeedAdGrid";
import OutfitService from "../services/outfit-service";
import SavedOutfitsService from "../services/saved-outfits-service";
import apiUtils from "../utils/apiUtils";
import FollowButton from "../components/Social/FollowButton";
import FollowingList from "../components/Social/FollowingList";
import FollowersList from "../components/Social/FollowersList";
import StyleLevelBadge from "../components/Gamification/StyleLevelBadge";
import BadgeShowcase from "../components/Gamification/BadgeShowcase";
import StreakDisplay from "../components/Gamification/StreakDisplay";
import { useGamificationStore, STYLE_LEVELS, BADGE_DEFINITIONS } from "../stores/gamificationStore";
import GamificationService from "../services/gamification-service";

// The API returns the user object directly, not wrapped
// interface UserApiResponse {
//   success: boolean;
//   user: User;
//   timestamp: string;
// }

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

/**
 * Tab panel component
 */
const TabPanel: React.FC<TabPanelProps> = ({
  children,
  value,
  index,
  ...other
}) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

/**
 * Profile page component
 */
const ProfilePage: React.FC = () => {
  const { userId: pathUserId } = useParams<{ userId?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user: currentUser, isLoading: authLoading } = useAuth();
  
  // Get userId from URL params - if present, it's viewing another user's profile
  const userId = pathUserId || searchParams.get('userId');
  
  // Get initial tab from URL parameter
  const initialTab = parseInt(searchParams.get('tab') || '0', 10);

  // State
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState<number>(initialTab);
  
  // Outfits state - initialize with empty arrays to prevent undefined access
  const [uploadedOutfits, setUploadedOutfits] = useState<Outfit[]>([]);
  const [likedOutfits, setLikedOutfits] = useState<Outfit[]>([]);
  const [savedOutfits, setSavedOutfits] = useState<Outfit[]>([]);
  const [isLoadingOutfits, setIsLoadingOutfits] = useState<boolean>(false);
  const [outfitsError, setOutfitsError] = useState<string | null>(null);

  // If no userId in URL, it's the current user's own profile
  const isOwnProfile = !userId;
  
  // Determine the effective user ID for API calls
  const effectiveUserId = userId || currentUser?.id;

  // Gamification profile
  const gamProfile = useGamificationStore((s) => s.profile);
  const setGamProfile = useGamificationStore((s) => s.setProfile);

  useEffect(() => {
    if (effectiveUserId) {
      GamificationService.fetchProfile(effectiveUserId).then(setGamProfile);
    }
  }, [effectiveUserId, setGamProfile]);

  // Update gamification profile with real activity data once outfits are loaded
  useEffect(() => {
    if (effectiveUserId && uploadedOutfits && uploadedOutfits.length > 0) {
      // Count unique active days this month from outfit uploads + today's login
      const now = new Date();
      const thisMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      const today = now.toISOString().split('T')[0];

      const allDates = uploadedOutfits
        .map((o) => {
          try { return new Date(o.createdAt).toISOString().split('T')[0]; } catch { return ''; }
        })
        .filter((d) => d.startsWith(thisMonth));

      // Add today as an active day (user is logged in right now)
      allDates.push(today);

      // Also add yesterday if user's lastLogin was yesterday
      if (user?.lastLogin) {
        try {
          const loginDate = new Date(user.lastLogin).toISOString().split('T')[0];
          if (loginDate.startsWith(thisMonth)) allDates.push(loginDate);
        } catch { /* ignore */ }
      }

      const uniqueActiveDays = Array.from(new Set(allDates)).length;

      // Total uploads this month
      const monthlyUploads = uploadedOutfits.filter((o) => {
        try { return new Date(o.createdAt).toISOString().startsWith(thisMonth); } catch { return false; }
      }).length;

      const profile = GamificationService.buildProfileFromActivity(
        effectiveUserId,
        uploadedOutfits.length, // total uploads (all time for badge)
        0, // ratings given
        uniqueActiveDays, // active days this month
      );
      setGamProfile(profile);
    }
  }, [effectiveUserId, uploadedOutfits, user, setGamProfile]);

  // Debug logging for user ID resolution
  useEffect(() => {
    console.log('ProfilePage: User ID resolution debug', {
      pathUserId,
      searchParamsUserId: searchParams.get('userId'),
      userId,
      currentUserId: currentUser?.id,
      effectiveUserId,
      isOwnProfile,
      authLoading,
      hasUser: !!user,
      userDisplayName: user?.displayName
    });
  }, [pathUserId, searchParams, userId, currentUser?.id, effectiveUserId, isOwnProfile, authLoading, user]);

  // Ensure likedOutfits is always an array with multiple safety checks
  const safeLikedOutfits = useMemo(() => {
    // Multiple layers of safety to prevent undefined access
    if (!likedOutfits) return [];
    if (!Array.isArray(likedOutfits)) return [];
    return likedOutfits;
  }, [likedOutfits]);

  // Ensure savedOutfits is always an array with multiple safety checks
  const safeSavedOutfits = useMemo(() => {
    if (!savedOutfits) return [];
    if (!Array.isArray(savedOutfits)) return [];
    return savedOutfits;
  }, [savedOutfits]);

  // Track AuthContext savedOutfits changes as a string for reliable dependency detection
  const currentUserSavedOutfitIds = currentUser?.savedOutfits?.join(',') || '';
  const currentUserLikedOutfitIds = currentUser?.likedOutfits?.join(',') || '';

  // Handle save/unsave from OutfitCard — directly update local state
  const handleOutfitSaveChange = (outfitId: string, saved: boolean) => {
    if (!saved) {
      setSavedOutfits(prev => prev.filter(o => o.id !== outfitId));
    }
  };

  // Handle like/unlike from OutfitCard — directly update local state
  const handleOutfitLikeChange = (outfitId: string, liked: boolean) => {
    if (!liked) {
      setLikedOutfits(prev => prev.filter(o => o.id !== outfitId));
    }
  };

  // Data fetched from backend API
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        console.log('ProfilePage: Fetching user data', { 
          userId, 
          isOwnProfile, 
          currentUserId: currentUser?.id, 
          effectiveUserId,
          authLoading,
          hasCurrentUser: !!currentUser
        });

        if (userId) {
          // Fetch other user's profile data from API
          console.log('ProfilePage: Fetching user profile for userId:', userId);
          try {
            const response = await apiUtils.get<User>(`/users/${userId}`);
            console.log('ProfilePage: Fetched user data:', response);
            
            // The API returns the user object directly, not wrapped
            if (response && response.id && response.displayName) {
              // Ensure dates are properly parsed
              const userData: User = {
                ...response,
                createdAt: new Date(response.createdAt),
                lastLogin: new Date(response.lastLogin),
                tryOnPictureUploadedAt: response.tryOnPictureUploadedAt ? new Date(response.tryOnPictureUploadedAt) : undefined
              };
              setUser(userData);
              console.log('ProfilePage: User state updated with:', userData.id, userData.displayName, 'createdAt:', userData.createdAt);
            } else {
              console.error('ProfilePage: Invalid user data received:', response);
              setError('Invalid user data received from API');
            }
          } catch (error) {
            console.error("Error fetching user profile:", error);
            setError("Failed to load user profile");
          }
        } else if (currentUser) {
          // No userId provided - show current user's profile
          console.log('ProfilePage: Showing current user profile', { 
            userId: currentUser.id, 
            displayName: currentUser.displayName 
          });
          setUser(currentUser);
        } else {
          // Handle case where we don't have userId or currentUser
          if (isOwnProfile) {
            if (authLoading) {
              // Still loading authentication, wait
              console.log('ProfilePage: Authentication still loading, waiting...');
              return;
            } else {
              // Authentication completed but no user - redirect to login
              console.log('ProfilePage: No authenticated user found, user needs to login');
              setError("Please sign in to view your profile");
            }
          } else {
            // Trying to view another user's profile but no userId provided
            console.error('ProfilePage: No userId provided for other user profile');
            setError("User not found");
          }
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        setError("Failed to load user profile");
      } finally {
        setIsLoading(false);
      }
    };

    // Only fetch if we have a userId OR if we have currentUser for own profile
    // Also wait for auth to complete before making decisions
    if (!authLoading) {
      if (userId || (isOwnProfile && currentUser)) {
        fetchUserData();
      } else if (isOwnProfile && !currentUser) {
        // Own profile but no current user - need to login
        console.log('ProfilePage: Own profile requested but no authenticated user');
        setIsLoading(false);
        setError("Please sign in to view your profile");
      } else if (!userId) {
        // No userId for other user profile
        console.log('ProfilePage: No user ID provided for profile page');
        setIsLoading(false);
        setError("User not found");
      }
    } else {
      // Still loading authentication
      console.log('ProfilePage: Waiting for authentication to complete...');
    }
  }, [userId, currentUser, isOwnProfile, authLoading, effectiveUserId]);

  // Fetch ALL outfit data when user loads (for accurate stats counters)
  // Only clear and re-fetch when the user changes, NOT on tab switch
  useEffect(() => {
    if (user) {
      // Clear previous user's data immediately
      setUploadedOutfits([]);
      setLikedOutfits([]);
      setSavedOutfits([]);
      setOutfitsError(null);
      
      // Fetch all data so stats counters always show correct totals
      fetchUploadedOutfits();
      fetchLikedOutfits();
      fetchSavedOutfits();
    }
  }, [user?.id]); // Only re-fetch when user changes, not on tab switch

  // Fetch user's uploaded outfits
  const fetchUploadedOutfits = async () => {
    if (!user) return;
    
    try {
      setIsLoadingOutfits(true);
      setOutfitsError(null);
      
      // Use the user ID from the loaded user object (most reliable)
      const targetUserId = user.id;
      
      console.log('ProfilePage: Fetching uploaded outfits for userId:', targetUserId, { isOwnProfile, userId, currentUserId: currentUser?.id });
      
      const result = await OutfitService.getUserOutfits(targetUserId, {
        limit: 50,
        sortBy: 'createdAt'
      });
      
      console.log('ProfilePage: Fetched uploaded outfits:', result);
      setUploadedOutfits(result.outfits);
    } catch (error) {
      console.error('Error fetching uploaded outfits:', error);
      setOutfitsError('Failed to load uploaded outfits');
    } finally {
      setIsLoadingOutfits(false);
    }
  };

  // Fetch user's liked outfits
  const fetchLikedOutfits = async () => {
    if (!user) return;
    
    try {
      setIsLoadingOutfits(true);
      setOutfitsError(null);
      
      // Use the most reliable user ID source - prioritize the loaded user's ID
      const targetUserId = user.id || userId || currentUser?.id;
      
      console.log('ProfilePage: Fetching liked outfits for userId:', targetUserId, { 
        isOwnProfile, 
        userId, 
        currentUserId: currentUser?.id,
        profileUserId: user?.id,
        finalTargetUserId: targetUserId
      });
      
      // Safety check - don't proceed if we don't have a user ID
      if (!targetUserId) {
        console.error('ProfilePage: No valid user ID found for liked outfits', {
          userIdFromUser: user?.id,
          userIdFromParams: userId,
          userIdFromAuth: currentUser?.id,
          isOwnProfile,
          hasUser: !!user
        });
        setOutfitsError('Unable to load liked outfits - user ID not available');
        setLikedOutfits([]);
        return;
      }
      
      const result = await OutfitService.getLikedOutfits(targetUserId);
      
      console.log('ProfilePage: Fetched liked outfits:', result);
      
      // Ensure we always set an array, with multiple layers of safety
      if (result && result.outfits && Array.isArray(result.outfits)) {
        setLikedOutfits(result.outfits);
      } else if (result && result.outfits && typeof result.outfits === 'object') {
        // If outfits exists but isn't an array, try to convert or default to empty
        console.warn('ProfilePage: Received non-array outfits data:', result.outfits);
        setLikedOutfits([]);
      } else {
        // No outfits in result or invalid data, set empty array
        console.log('ProfilePage: No valid outfits data in result, setting empty array');
        setLikedOutfits([]);
      }
    } catch (error) {
      console.error('Error fetching liked outfits:', error);
      setOutfitsError('Failed to load liked outfits');
      // Set empty array on error to prevent undefined access
      setLikedOutfits([]);
    } finally {
      setIsLoadingOutfits(false);
    }
  };

  // Fetch user's saved outfits
  const fetchSavedOutfits = async () => {
    if (!user) return;
    
    try {
      setIsLoadingOutfits(true);
      setOutfitsError(null);
      
      const targetUserId = user.id || userId || currentUser?.id;
      
      console.log('ProfilePage: Fetching saved outfits for userId:', targetUserId);
      
      if (!targetUserId) {
        console.error('ProfilePage: No valid user ID found for saved outfits');
        setOutfitsError('Unable to load saved outfits - user ID not available');
        setSavedOutfits([]);
        return;
      }
      
      const result = await SavedOutfitsService.getSavedOutfits(targetUserId);
      
      console.log('ProfilePage: Fetched saved outfits:', result);
      
      if (result && result.outfits && Array.isArray(result.outfits)) {
        setSavedOutfits(result.outfits);
      } else if (result && result.outfits && typeof result.outfits === 'object') {
        console.warn('ProfilePage: Received non-array saved outfits data:', result.outfits);
        setSavedOutfits([]);
      } else {
        console.log('ProfilePage: No valid saved outfits data in result, setting empty array');
        setSavedOutfits([]);
      }
    } catch (error) {
      console.error('Error fetching saved outfits:', error);
      setOutfitsError('Failed to load saved outfits');
      setSavedOutfits([]);
    } finally {
      setIsLoadingOutfits(false);
    }
  };

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    
    // Only fetch if data hasn't been loaded yet (e.g. if initial fetch failed)
    if (newValue === 0 && (!uploadedOutfits || uploadedOutfits.length === 0)) {
      fetchUploadedOutfits();
    } else if (newValue === 1 && (!likedOutfits || likedOutfits.length === 0)) {
      fetchLikedOutfits();
    } else if (newValue === 2 && (!savedOutfits || savedOutfits.length === 0)) {
      fetchSavedOutfits();
    }
  };

  // Refresh outfits
  const handleRefreshOutfits = () => {
    if (tabValue === 0) {
      fetchUploadedOutfits();
    } else if (tabValue === 1) {
      fetchLikedOutfits();
    } else if (tabValue === 2) {
      fetchSavedOutfits();
    }
  };

  // Loading state - show loading if we're fetching data OR waiting for auth context
  if (isLoading || (isOwnProfile && (authLoading || (!currentUser && !error)))) {
    return (
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
          <Skeleton
            variant="circular"
            width={100}
            height={100}
            sx={{ mr: 3 }}
          />
          <Box>
            <Skeleton variant="text" width={200} height={40} />
            <Skeleton variant="text" width={150} height={24} />
          </Box>
        </Box>
        <Skeleton variant="rectangular" height={400} />
      </Box>
    );
  }

  // Error state
  if (error || (!user && !isLoading && !authLoading)) {
    const isAuthError = error?.includes("sign in") || error?.includes("login");
    
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "50vh",
        }}
      >
        <Typography variant="h5" color="error" gutterBottom>
          {error || "User not found"}
        </Typography>
        
        {isAuthError ? (
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => navigate('/login')}
            sx={{ mt: 2 }}
          >
            Sign In
          </Button>
        ) : (
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => navigate('/')}
            sx={{ mt: 2 }}
          >
            Back to Home
          </Button>
        )}
      </Box>
    );
  }

  // Additional safety check for TypeScript
  if (!user) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "50vh",
        }}
      >
        <Typography variant="h5" color="error" gutterBottom>
          User data not available
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Profile header */}
      <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
          <Avatar
            src={user.profilePicture}
            alt={user.displayName}
            sx={{ width: 100, height: 100, mr: 3, mb: { xs: 2, sm: 0 } }}
          />

          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
              <Typography variant="h4">
                {user.displayName}
              </Typography>
              {gamProfile && <StyleLevelBadge level={gamProfile.styleLevel} size="small" />}
            </Box>

            {/* Stats bar */}
            <Box sx={{ display: 'flex', gap: 3, mb: 1 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1 }}>{uploadedOutfits?.length || 0}</Typography>
                <Typography variant="caption" color="text.secondary">Outfits</Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1 }}>{safeSavedOutfits?.length || 0}</Typography>
                <Typography variant="caption" color="text.secondary">Saved</Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1 }}>{user.followerCount || 0}</Typography>
                <Typography variant="caption" color="text.secondary">Followers</Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1 }}>{user.followingCount || 0}</Typography>
                <Typography variant="caption" color="text.secondary">Following</Typography>
              </Box>
            </Box>

            {/* Monthly Drip Challenge */}
            {gamProfile && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  🎯 Monthly Drip Challenge — {new Date().toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
                </Typography>
                <BadgeShowcase badges={gamProfile.badges} />
              </Box>
            )}

            {/* Streak */}
            {gamProfile && (
              <Box sx={{ mt: 1 }}>
                <StreakDisplay
                  currentStreak={gamProfile.streak.currentStreak}
                  longestStreak={gamProfile.streak.longestStreak}
                  isBroken={gamProfile.streak.isBroken}
                />
              </Box>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Member since {(() => {
                const date = user.createdAt ? new Date(user.createdAt) : null;
                if (date && !isNaN(date.getTime()) && date.getFullYear() > 2000) {
                  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
                }
                return new Date('2026-01-01').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
              })()}
            </Typography>
          </Box>

          {isOwnProfile ? (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              sx={{ ml: { xs: 0, sm: 2 }, mt: { xs: 2, sm: 0 } }}
              onClick={() => navigate('/settings')}
            >
              Edit Profile
            </Button>
          ) : (
            <Box sx={{ ml: { xs: 0, sm: 2 }, mt: { xs: 2, sm: 0 } }}>
              {(() => {
                // Debug logging for follow button user ID
                console.log('🔍 ProfilePage: FollowButton render debug', {
                  isOwnProfile,
                  userId,
                  pathUserId,
                  currentUserId: currentUser?.id,
                  effectiveUserId,
                  userFromState: user?.id,
                  userDisplayName: user?.displayName,
                  targetUserIdForButton: user?.id,
                  timestamp: new Date().toISOString()
                });
                
                return user?.id ? (
                  <FollowButton
                    targetUserId={user.id}
                    size="large"
                    variant="contained"
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    ❌ No user ID for follow button
                  </Typography>
                );
              })()}
            </Box>
          )}
        </Box>
      </Paper>

      {/* Profile tabs */}
      <Box sx={{ width: "100%" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="profile tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Uploaded Outfits" />
            <Tab label="Liked Outfits" />
            <Tab label="Saved" />
            <Tab label="Following" />
            <Tab label="Followers" />
            {isOwnProfile && <Tab label="Settings" />}
          </Tabs>
        </Box>

        {/* Uploaded outfits tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Uploaded Outfits ({uploadedOutfits?.length || 0})
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={handleRefreshOutfits}
              disabled={isLoadingOutfits}
            >
              Refresh
            </Button>
          </Box>

          {outfitsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {outfitsError}
            </Alert>
          )}

          {/* Loading state */}
          {isLoadingOutfits ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            /* User's uploaded outfits */
            <>
            <Grid container spacing={3}>
              {(!uploadedOutfits || uploadedOutfits.length === 0) ? (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 200,
                      bgcolor: "background.paper",
                      borderRadius: 1,
                      border: "1px dashed",
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant="body1" color="text.secondary" gutterBottom>
                      {isOwnProfile ? "You haven't uploaded any outfits yet" : "This user hasn't uploaded any outfits yet"}
                    </Typography>
                    {isOwnProfile && (
                      <Button variant="contained" color="primary" href="/upload" sx={{ mt: 2 }}>
                        Upload Your First Outfit
                      </Button>
                    )}
                  </Box>
                </Grid>
              ) : null}
            </Grid>
            {uploadedOutfits && uploadedOutfits.length > 0 && (
              <InFeedAdGrid
                pageContext="profile"
                gridItemProps={{ xs: 12, sm: 6, md: 4, lg: 3 }}
              >
                {(uploadedOutfits || []).map((outfit) => (
                  <OutfitCard key={outfit.id} outfit={outfit} showUserInfo={!isOwnProfile} onSaveChange={handleOutfitSaveChange} onLikeChange={handleOutfitLikeChange} />
                ))}
              </InFeedAdGrid>
            )}
            </>
          )}
        </TabPanel>

        {/* Liked outfits tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Liked Outfits ({safeLikedOutfits?.length || 0})
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={handleRefreshOutfits}
              disabled={isLoadingOutfits}
            >
              Refresh
            </Button>
          </Box>

          {outfitsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {outfitsError}
            </Alert>
          )}

          {/* Loading state */}
          {isLoadingOutfits ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            /* User's liked outfits */
            <>
            <Grid container spacing={3}>
              {(!safeLikedOutfits || safeLikedOutfits.length === 0) ? (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 200,
                      bgcolor: "background.paper",
                      borderRadius: 1,
                      border: "1px dashed",
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant="body1" color="text.secondary" gutterBottom>
                      {isOwnProfile ? "You haven't liked any outfits yet" : "This user hasn't liked any outfits yet"}
                    </Typography>
                    {isOwnProfile && (
                      <Button variant="contained" color="primary" href="/search" sx={{ mt: 2 }}>
                        Discover Outfits to Like
                      </Button>
                    )}
                  </Box>
                </Grid>
              ) : null}
            </Grid>
            {safeLikedOutfits && safeLikedOutfits.length > 0 && (
              <InFeedAdGrid
                pageContext="profile"
                gridItemProps={{ xs: 12, sm: 6, md: 4, lg: 3 }}
              >
                {(safeLikedOutfits || []).map((outfit) => (
                  <OutfitCard key={outfit.id} outfit={outfit} showUserInfo={true} onSaveChange={handleOutfitSaveChange} onLikeChange={handleOutfitLikeChange} />
                ))}
              </InFeedAdGrid>
            )}
            </>
          )}
        </TabPanel>

        {/* Saved outfits tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Saved Outfits ({safeSavedOutfits?.length || 0})
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={handleRefreshOutfits}
              disabled={isLoadingOutfits}
            >
              Refresh
            </Button>
          </Box>

          {outfitsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {outfitsError}
            </Alert>
          )}

          {/* Loading state */}
          {isLoadingOutfits ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            /* User's saved outfits */
            <>
            <Grid container spacing={3}>
              {(!safeSavedOutfits || safeSavedOutfits.length === 0) ? (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 200,
                      bgcolor: "background.paper",
                      borderRadius: 1,
                      border: "1px dashed",
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant="body1" color="text.secondary" gutterBottom>
                      {isOwnProfile ? "You haven't saved any outfits yet" : "This user hasn't saved any outfits yet"}
                    </Typography>
                    {isOwnProfile && (
                      <Button variant="contained" color="primary" href="/search" sx={{ mt: 2 }}>
                        Discover Outfits
                      </Button>
                    )}
                  </Box>
                </Grid>
              ) : null}
            </Grid>
            {safeSavedOutfits && safeSavedOutfits.length > 0 && (
              <InFeedAdGrid
                pageContext="profile"
                gridItemProps={{ xs: 12, sm: 6, md: 4, lg: 3 }}
              >
                {(safeSavedOutfits || []).map((outfit) => (
                  <OutfitCard key={outfit.id} outfit={outfit} showUserInfo={true} onSaveChange={handleOutfitSaveChange} onLikeChange={handleOutfitLikeChange} />
                ))}
              </InFeedAdGrid>
            )}
            </>
          )}
        </TabPanel>

        {/* Following tab */}
        <TabPanel value={tabValue} index={3}>
          {effectiveUserId ? (
            <FollowingList
              userId={effectiveUserId}
              title="Following"
              showSearch={true}
              maxHeight={600}
              onUserClick={(clickedUserId) => {
                // Navigate to clicked user's profile using React Router
                navigate(`/profile/${clickedUserId}`);
              }}
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                height: 200,
                bgcolor: "background.paper",
                borderRadius: 1,
                border: "1px dashed",
                borderColor: "divider",
              }}
            >
              <Typography variant="body1" color="text.secondary">
                ❌ ProfilePage: No user ID available for following list
              </Typography>
            </Box>
          )}
        </TabPanel>

        {/* Followers tab */}
        <TabPanel value={tabValue} index={4}>
          {effectiveUserId ? (
            <FollowersList
              userId={effectiveUserId}
              title="Followers"
              showSearch={true}
              maxHeight={600}
              onUserClick={(clickedUserId) => {
                // Navigate to clicked user's profile using React Router
                navigate(`/profile/${clickedUserId}`);
              }}
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                height: 200,
                bgcolor: "background.paper",
                borderRadius: 1,
                border: "1px dashed",
                borderColor: "divider",
              }}
            >
              <Typography variant="body1" color="text.secondary">
                ❌ ProfilePage: No user ID available for followers list
              </Typography>
            </Box>
          )}
        </TabPanel>

        {/* Settings tab (only for own profile) */}
        {isOwnProfile && (
          <TabPanel value={tabValue} index={5}>
            <Typography variant="h6" gutterBottom>
              Account Settings
            </Typography>

            <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Personal Information
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Display Name
                  </Typography>
                  <Typography variant="body1">{user.displayName}</Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body1">{user.email}</Typography>
                </Grid>
              </Grid>

              <Button
                variant="outlined"
                size="small"
                startIcon={<EditIcon />}
                sx={{ mt: 2 }}
              >
                Edit
              </Button>
            </Paper>

            <Paper elevation={1} sx={{ p: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Account Security
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Button variant="outlined" color="primary" sx={{ mr: 2 }}>
                Change Password
              </Button>

              <Button variant="outlined" color="error">
                Delete Account
              </Button>
            </Paper>
          </TabPanel>
        )}
      </Box>
    </Box>
  );
};

export default ProfilePage;
